import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaHandshake, FaArrowLeft, FaEdit, FaPhone, FaFax, FaEnvelope,
  FaMapMarkerAlt, FaSave, FaTimes, FaUser, FaGlobe, FaBriefcase,
  FaPlus, FaTrash, FaClock
} from 'react-icons/fa';
import './SheriffOfficeDetail.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BailAgencyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editedAgency, setEditedAgency] = useState(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [newStaff, setNewStaff] = useState({
    name: '',
    title: '',
    phone: '',
    phone_ext: '',
    email: '',
    notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchAgencyDetails();
  }, [id]);

  const fetchAgencyDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/bail-agencies/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setAgency(data.data);
        setEditedAgency(data.data);
        setStaff(data.data.staff || []);
        setError('');
      } else {
        setError('Bail agency not found');
      }
    } catch (err) {
      console.error('Error fetching agency details:', err);
      setError('Failed to load agency details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAgencyEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/bail-agencies/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editedAgency)
      });

      const data = await response.json();

      if (data.success) {
        setAgency(data.data);
        setEditedAgency(data.data);
        setEditMode(false);
      } else {
        alert('Failed to update agency');
      }
    } catch (err) {
      console.error('Error updating agency:', err);
      alert('Failed to update agency');
    }
  };

  const handleCancelEdit = () => {
    setEditedAgency(agency);
    setEditMode(false);
  };

  const handleInputChange = (field, value) => {
    setEditedAgency(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddStaff = async () => {
    if (!newStaff.name || !newStaff.title) {
      alert('Name and title are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/bail-agencies/${id}/staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newStaff)
      });

      const data = await response.json();

      if (data.success) {
        // Refresh agency details to get updated staff list
        await fetchAgencyDetails();
        // Reset form and close modal
        setNewStaff({
          name: '',
          title: '',
          phone: '',
          phone_ext: '',
          email: '',
          notes: ''
        });
        setShowAddStaffModal(false);
      } else {
        alert('Failed to add staff member');
      }
    } catch (err) {
      console.error('Error adding staff:', err);
      alert('Failed to add staff member');
    }
  };

  const handleStaffInputChange = (field, value) => {
    setNewStaff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getTitleBadge = (title) => {
    if (!title) return null;

    const titleLower = title.toLowerCase();
    let color = '#7f8c8d';

    if (titleLower.includes('owner') || titleLower.includes('principal')) {
      color = '#f39c12';
    } else if (titleLower.includes('agent') || titleLower.includes('bondsman')) {
      color = '#2c3e50';
    } else if (titleLower.includes('manager') || titleLower.includes('director')) {
      color = '#34495e';
    }

    return (
      <span className="title-badge" style={{ backgroundColor: color }}>
        {title}
      </span>
    );
  };

  // Sort staff by contact_order, then by name
  const sortedStaff = [...staff].sort((a, b) => {
    if (a.contact_order !== b.contact_order) {
      return a.contact_order - b.contact_order;
    }
    return (a.name || '').localeCompare(b.name || '');
  });

  if (loading) {
    return (
      <div className="office-detail-container">
        <div className="loading">Loading agency details...</div>
      </div>
    );
  }

  if (error || !agency) {
    return (
      <div className="office-detail-container">
        <div className="error-message">{error || 'Agency not found'}</div>
        <button className="btn-back" onClick={() => navigate('/address-book/bail-agencies')}>
          <FaArrowLeft /> Back to Bail Agencies
        </button>
      </div>
    );
  }

  return (
    <div className="office-detail-container">
      {/* Header */}
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/address-book/bail-agencies')}>
          <FaArrowLeft /> Back to Bail Agencies
        </button>

        <div className="header-title">
          <FaHandshake size={28} style={{ color: '#f39c12' }} />
          <div>
            <h1>{agency.name}</h1>
            {agency.dba && <p className="dba-text" style={{ margin: '0.25rem 0 0 0' }}>DBA: {agency.dba}</p>}
            <div className="header-badges">
              {agency.county && <span className="county-badge-large">{agency.county} County</span>}
              {agency.available_24_7 && (
                <span className="badge-24-7" style={{ marginLeft: '0.5rem' }}>
                  <FaClock /> 24/7 Service
                </span>
              )}
            </div>
          </div>
        </div>

        {user.role === 'admin' && !editMode && (
          <button className="btn-edit-office" onClick={() => setEditMode(true)}>
            <FaEdit /> Edit Agency Info
          </button>
        )}

        {editMode && (
          <div className="edit-actions">
            <button className="btn-save" onClick={handleSaveAgencyEdit}>
              <FaSave /> Save
            </button>
            <button className="btn-cancel" onClick={handleCancelEdit}>
              <FaTimes /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="content-layout">
        {/* Agency Information Section */}
        <div className="office-info-section">
          <h2>Agency Information</h2>

          {!editMode ? (
            <div className="info-card-compact">
              <div className="compact-grid">
                {agency.owner && (
                  <div className="compact-item full-width">
                    <label><FaUser /> Owner</label>
                    <span className="owner-highlight">{agency.owner}</span>
                  </div>
                )}

                <div className="compact-item">
                  <label>Address</label>
                  <span>
                    {agency.address_street || 'N/A'}
                    <br />
                    {agency.address_city || agency.city}, {agency.address_state || 'MT'} {agency.address_zip}
                  </span>
                </div>

                <div className="compact-item">
                  <label><FaPhone /> Phone</label>
                  <span>{agency.phone || 'N/A'}</span>
                </div>

                <div className="compact-item">
                  <label><FaFax /> Fax</label>
                  <span>{agency.fax || 'N/A'}</span>
                </div>

                <div className="compact-item">
                  <label><FaEnvelope /> Email</label>
                  <span>
                    {agency.email ? (
                      <a href={`mailto:${agency.email}`}>{agency.email}</a>
                    ) : 'N/A'}
                  </span>
                </div>

                <div className="compact-item full-width">
                  <label><FaGlobe /> Website</label>
                  <span>
                    {agency.website ? (
                      <a href={agency.website} target="_blank" rel="noopener noreferrer">
                        {agency.website}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
              </div>

              {agency.notes && (
                <>
                  <div className="info-divider"></div>
                  <div className="compact-item full-width">
                    <label>Notes</label>
                    <p className="notes-text">{agency.notes}</p>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="edit-form">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Agency Name *</label>
                  <input
                    type="text"
                    value={editedAgency.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>DBA (Doing Business As)</label>
                  <input
                    type="text"
                    value={editedAgency.dba || ''}
                    onChange={(e) => handleInputChange('dba', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Owner</label>
                  <input
                    type="text"
                    value={editedAgency.owner || ''}
                    onChange={(e) => handleInputChange('owner', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>County</label>
                  <input
                    type="text"
                    value={editedAgency.county || ''}
                    onChange={(e) => handleInputChange('county', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={editedAgency.city || ''}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={editedAgency.address_street || ''}
                    onChange={(e) => handleInputChange('address_street', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Address City</label>
                  <input
                    type="text"
                    value={editedAgency.address_city || ''}
                    onChange={(e) => handleInputChange('address_city', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={editedAgency.address_state || 'MT'}
                    onChange={(e) => handleInputChange('address_state', e.target.value)}
                    maxLength="2"
                  />
                </div>

                <div className="form-group">
                  <label>ZIP Code</label>
                  <input
                    type="text"
                    value={editedAgency.address_zip || ''}
                    onChange={(e) => handleInputChange('address_zip', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={editedAgency.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Fax</label>
                  <input
                    type="tel"
                    value={editedAgency.fax || ''}
                    onChange={(e) => handleInputChange('fax', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editedAgency.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    value={editedAgency.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={editedAgency.available_24_7 || false}
                      onChange={(e) => handleInputChange('available_24_7', e.target.checked)}
                      style={{ width: 'auto', marginRight: '0.5rem' }}
                    />
                    24/7 Service Available
                  </label>
                </div>

                <div className="form-group full-width">
                  <label>Notes</label>
                  <textarea
                    value={editedAgency.notes || ''}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows="4"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Staff Section */}
        <div className="office-staff-section">
          <div className="staff-header">
            <h2>Agency Staff</h2>
            {user.role === 'admin' && (
              <button className="btn-add-staff" onClick={() => setShowAddStaffModal(true)}>
                <FaPlus /> Add Staff
              </button>
            )}
          </div>

          <div className="staff-list">
            {sortedStaff.length === 0 ? (
              <div className="no-staff-info">
                <FaUser size={48} />
                <p>No staff contacts available</p>
              </div>
            ) : (
              sortedStaff.map((member) => (
                <div key={member.id} className="staff-card">
                  <div className="staff-card-header">
                    <div className="staff-name-section">
                      <h3>{member.name}</h3>
                    </div>
                    {getTitleBadge(member.title)}
                  </div>

                  <div className="staff-contact-info">
                    {member.phone && (
                      <div className="staff-contact-item">
                        <FaPhone className="staff-icon" />
                        <span>
                          {member.phone}
                          {member.phone_ext && ` ext. ${member.phone_ext}`}
                        </span>
                      </div>
                    )}

                    {member.email && (
                      <div className="staff-contact-item">
                        <FaEnvelope className="staff-icon" />
                        <a href={`mailto:${member.email}`}>{member.email}</a>
                      </div>
                    )}

                    {member.notes && (
                      <div className="staff-notes">
                        {member.notes}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Add Staff Modal */}
      {showAddStaffModal && (
        <div className="modal-overlay" onClick={() => setShowAddStaffModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Staff Member</h2>
              <button className="modal-close" onClick={() => setShowAddStaffModal(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={newStaff.name}
                  onChange={(e) => handleStaffInputChange('name', e.target.value)}
                  placeholder="Enter staff member name"
                />
              </div>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newStaff.title}
                  onChange={(e) => handleStaffInputChange('title', e.target.value)}
                  placeholder="e.g., Agent, Bondsman, Office Manager"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newStaff.phone}
                    onChange={(e) => handleStaffInputChange('phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>

                <div className="form-group">
                  <label>Extension</label>
                  <input
                    type="text"
                    value={newStaff.phone_ext}
                    onChange={(e) => handleStaffInputChange('phone_ext', e.target.value)}
                    placeholder="Ext."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newStaff.email}
                  onChange={(e) => handleStaffInputChange('email', e.target.value)}
                  placeholder="Email address"
                />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={newStaff.notes}
                  onChange={(e) => handleStaffInputChange('notes', e.target.value)}
                  placeholder="Additional notes..."
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddStaffModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleAddStaff}>
                <FaPlus /> Add Staff Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BailAgencyDetail;
